import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useAnimation, useMotionValue, useTransform } from 'framer-motion';
import Button from '../common/Button';

const HeroContainer = styled.section`
  position: relative;
  height: 100vh;
  min-height: 700px;
  background: var(--gradient-hero);
  color: white;
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-top: var(--header-height);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 80%, rgba(102, 102, 102, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(51, 51, 51, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 600px;
    padding: var(--space-16) 0;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.8;
  overflow: hidden;

  .aurora {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(
        ellipse 200% 100% at 90% 10%,
        rgba(102, 102, 102, 0.15) 10%,
        rgba(51, 51, 51, 0) 40%
      ),
      radial-gradient(
        ellipse 200% 100% at 20% 0%,
        rgba(51, 51, 51, 0.2) 15%,
        rgba(0, 0, 0, 0) 40%
      ),
      radial-gradient(
        ellipse 200% 100% at 100% 0%,
        rgba(102, 102, 102, 0.1) 15%,
        rgba(51, 51, 51, 0) 40%
      );
    animation: aurora 20s linear infinite;
  }

  @keyframes aurora {
    0% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(200px, 100px);
    }
    100% {
      transform: translate(0, 0);
    }
  }
`;

const GlowingOrb = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '400px'};
  height: ${props => props.size || '400px'};
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${props => props.color || 'rgba(102, 102, 102, 0.2)'} 0%,
    rgba(0, 0, 0, 0) 70%
  );
  filter: blur(40px);
  z-index: 0;
`;

const ShapeDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
  z-index: 2;
  
  svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
    height: 100px;
    transform: rotateY(180deg);
  }
  
  .shape-fill {
    fill: var(--background-light);
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--space-6);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--space-8);
  }
`;

const HeroContent = styled.div`
  @media (max-width: 992px) {
    order: 2;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: var(--font-size-6xl);
  font-weight: 900;
  margin-bottom: var(--space-6);
  line-height: 1.1;
  color: white;
  background: none;
  -webkit-text-fill-color: initial;
  
  span {
    background: linear-gradient(135deg, var(--accent-color) 0%, var(--accent-light) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    display: inline-block;
  }
  
  span::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(135deg, var(--accent-color) 0%, var(--accent-light) 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 1200px) {
    font-size: var(--font-size-5xl);
  }
  
  @media (max-width: 768px) {
    font-size: var(--font-size-4xl);
  }
  
  @media (max-width: 576px) {
    font-size: var(--font-size-3xl);
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: var(--font-size-xl);
  margin-bottom: var(--space-8);
  color: rgba(255, 255, 255, 0.95);
  max-width: 600px;
  letter-spacing: 0.02em;
  line-height: 1.6;
  font-weight: 400;
  
  @media (max-width: 992px) {
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 768px) {
    font-size: var(--font-size-lg);
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: var(--space-4);
  
  @media (max-width: 992px) {
    justify-content: center;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeroImageContainer = styled(motion.div)`
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius-xl);
  overflow: hidden;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
  perspective: 1000px;
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(26, 54, 93, 0.3) 0%, rgba(246, 173, 85, 0.2) 100%);
    pointer-events: none;
  }
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: var(--space-6);
    height: 400px;
  }
`;

const DashboardMockup = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
`;

const ChartContainer = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--border-radius-lg);
  padding: var(--space-4);
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border-color);
`;

const DataChart = () => (
  <ChartContainer
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.5, duration: 0.6 }}
    style={{
      top: '10%',
      left: '10%',
      width: '200px',
      height: '120px'
    }}
  >
    <div style={{ 
      width: '100%', 
      height: '60px', 
      background: 'linear-gradient(135deg, var(--primary-color), var(--accent-color))',
      borderRadius: '8px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        bottom: '0',
        left: '0',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
        animation: 'shimmer 2s infinite'
      }} />
    </div>
    <div style={{ 
      marginTop: '8px', 
      fontSize: '12px', 
      color: 'var(--text-medium)',
      fontWeight: '600'
    }}>
      Position Limits
    </div>
  </ChartContainer>
);

const NetworkSVG = () => (
  <ChartContainer
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.7, duration: 0.6 }}
    style={{
      bottom: '15%',
      right: '15%',
      width: '180px',
      height: '90px',
      padding: '12px',
    }}
  >
    <svg width="100%" height="40" viewBox="0 0 160 40">
      <circle cx="30" cy="20" r="8" fill="var(--primary-color)" />
      <circle cx="80" cy="10" r="6" fill="var(--accent-color)" />
      <circle cx="130" cy="30" r="7" fill="var(--primary-light)" />
      <line x1="38" y1="20" x2="74" y2="10" stroke="var(--primary-color)" strokeWidth="2" />
      <line x1="86" y1="10" x2="123" y2="30" stroke="var(--accent-color)" strokeWidth="2" />
      <line x1="30" y1="28" x2="130" y2="30" stroke="var(--primary-light)" strokeWidth="2" />
    </svg>
    <div
      style={{
        fontSize: '12px',
        color: 'var(--text-medium)',
        fontWeight: '600',
        marginTop: '8px',
      }}
    >
      Market Network
    </div>
  </ChartContainer>
);

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const controls = useAnimation();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    });

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [controls]);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [0, 100], [15, -15]);
  const rotateY = useTransform(x, [0, 100], [-15, 15]);

  return (
    <HeroContainer>
      <AnimatedBackground>
        <div className="aurora"></div>
        <GlowingOrb
          size="600px"
          color="rgba(246, 173, 85, 0.15)"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            top: '10%',
            left: '10%',
          }}
        />
        <GlowingOrb
          size="400px"
          color="rgba(45, 90, 139, 0.1)"
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            top: '60%',
            right: '20%',
          }}
        />
      </AnimatedBackground>

      <ContentWrapper>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            US Energy Trading <span>Compliance</span> Solutions
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Leading provider of position limits monitoring, CFTC compliance, and regulatory reporting solutions for US gas and energy commodity trading firms.
          </HeroSubtitle>
          
          <ButtonGroup
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Button
              primary
              to="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started
            </Button>
            <Button
              secondary
              to="/services"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </Button>
          </ButtonGroup>
        </HeroContent>

        <HeroImageContainer
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{
            rotateX,
            rotateY,
          }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const mouseX = e.clientX - centerX;
            const mouseY = e.clientY - centerY;
            
            x.set((mouseX / (rect.width / 2)) * 100);
            y.set((mouseY / (rect.height / 2)) * 100);
          }}
          onMouseLeave={() => {
            x.set(0);
            y.set(0);
          }}
        >
          <DashboardMockup>
            <DataChart />
            <NetworkSVG />
          </DashboardMockup>
        </HeroImageContainer>
      </ContentWrapper>

      <ShapeDivider>
        <svg
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="shape-fill"
          ></path>
        </svg>
      </ShapeDivider>
    </HeroContainer>
  );
};

export default Hero; 